import type { AppointmentTooltip } from '@devexpress/dx-react-scheduler-material-ui';
import isBefore from 'date-fns/isBefore';
import dayjs from 'dayjs';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';
import { InlineMessage } from '@components/InlineMessage';
import {
  HorizontalFlex,
  VerticalFlex,
} from '@components/RecordCard/RecordCard.style';

import { ROUTINES } from '@constants/routes';

import useRoutines from '@hooks/useRoutines';

import type { RoutineEvent } from '@models/routine';
import { RoutineRecord } from '@models/routine';

import { ContentWrapper, TooltipTitle } from './AppointmentTooltip.style';

const messages = defineMessages({
  completed: {
    id: 'GLOBAL.completed',
    defaultMessage: 'Completed',
  },
  notCompleted: {
    id: 'GLOBAL.notCompleted',
    defaultMessage: 'Not completed',
  },
});

const Content = ({ ...props }: AppointmentTooltip.ContentProps) => {
  const { formatMessage } = useIntl();
  const { isRoutineComplete } = useRoutines();
  const { appointmentData } = props;
  if (appointmentData) {
    const routineEvent = { ...(appointmentData as RoutineEvent) };
    const done = isRoutineComplete(routineEvent);
    const { startDate, endDate, title } = routineEvent;
    const upcoming = isBefore(new Date(), new Date(startDate));
    const timeSlots = RoutineRecord.getTimeSlots(routineEvent);
    const currentTimeSlot = timeSlots.find(
      ({ startTime }) =>
        startTime.toISOString() === new Date(startDate).toISOString()
    );

    return (
      <ContentWrapper>
        <HorizontalFlex
          border
          clickable
          onClick={() => {
            navigate(`${ROUTINES}${routineEvent.id}`);
          }}
        >
          <VerticalFlex alignItems="flex-start">
            <TooltipTitle>{title}</TooltipTitle>
          </VerticalFlex>
          <VerticalFlex alignItems="flex-end">
            <Icon
              clickable
              color="text.primary"
              name="chevron-down"
              rotation={-90}
            />
          </VerticalFlex>
        </HorizontalFlex>
        <HorizontalFlex justifyContent="flex-start">
          <VerticalFlex alignItems="flex-start" paddingLeft={18}>
            <Icon color="text.secondary" name="calendar-day" />
          </VerticalFlex>
          <VerticalFlex alignItems="flex-start" paddingLeft={18}>
            {`${dayjs(currentTimeSlot?.startTime ?? startDate).format(
              'MMMM D, YYYY'
            )}`}
          </VerticalFlex>
        </HorizontalFlex>
        <HorizontalFlex justifyContent="flex-start">
          <VerticalFlex alignItems="flex-start" paddingLeft={18}>
            <Icon color="text.secondary" name="alarm-clock" />
          </VerticalFlex>
          <VerticalFlex alignItems="flex-start" paddingLeft={18}>
            {`${dayjs(currentTimeSlot?.startTime ?? startDate).format('LT')}${
              (currentTimeSlot?.endTime ?? endDate)
                ? ` - ${dayjs(currentTimeSlot?.endTime ?? endDate).format(
                    'LT'
                  )}`
                : ''
            }`}
          </VerticalFlex>
        </HorizontalFlex>
        {!upcoming && (
          <HorizontalFlex justifyContent="flex-start">
            <VerticalFlex alignItems="flex-start" paddingLeft={18}>
              <InlineMessage
                message={formatMessage(
                  done ? messages.completed : messages.notCompleted
                )}
                padding="0 0 0 20px"
                type={done ? 'success' : 'error'}
              />
            </VerticalFlex>
          </HorizontalFlex>
        )}
      </ContentWrapper>
    );
  }
  return <div />;
};

export default Content;
